import { CSSProperties } from 'react';
import { clsx } from 'clsx';
import { Menu, Tooltip, MenuItem, Icon, PopperPlacementType } from '@hevoio/hevo-ui-toolkit';
import { EntityAction } from '../../interfaces/entity-actions';
import styles from './EntityMenu.module.scss';

interface EntityMenuProps<T> {
  open: boolean;
  target: string;
  placement: PopperPlacementType;
  actions: EntityAction<T>[];
  style?: CSSProperties;
  onClose: VoidFunction;
  onActionClick: (action: EntityAction<T>) => void;
}

export function EntityMenu<T>({
  target,
  open,
  onClose,
  style,
  placement,
  actions,
  onActionClick
}: EntityMenuProps<T>) {
  const firstDestructiveIndex = actions.findIndex(action => action.destructive);

  return (
    <Menu
      target={`#${target}`}
      open={open}
      onClose={() => onClose()}
      dataId=''
      placement={placement}
      style={style}
    >
      {actions.map((action, index) => {
        const destructiveClass = action.destructive ? 'text-error' : undefined;

        return (
          <Tooltip
            dataId={action.type}
            placement='left'
            title={action.disabledReason}
            disabled={!action.disabled}
            key={String(action.action)}
          >
            <div>
              <MenuItem
                dataId={action.type}
                // Toolkit MenuItem's own top rule. Never on the first item, so a
                // menu whose only action is Delete doesn't open with a divider.
                className={clsx(
                  index > 0 && index === firstDestructiveIndex && 'has-separator'
                )}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();

                  onActionClick(action);
                  onClose();
                }}
                disabled={action.disabled}
                selected={false}
              >
                <div className='d-flex align-items-center'>
                  <span className={styles.iconFrame}>
                    <Icon
                      name={action.icon}
                      className={clsx(destructiveClass, action.className)}
                      dataId={action.type}
                    />
                  </span>

                  <div className='flex-col ml-3'>
                    <span className={clsx('text-medium', destructiveClass, action.className)}>
                      {action.displayName}
                    </span>

                    <span className='mt-1 text-secondary text-normal text-wrap'>
                      {action.description}
                    </span>
                  </div>
                </div>
              </MenuItem>
            </div>
          </Tooltip>
        );
      })}
    </Menu>
  );
}
